import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * FAR Print Shop Header Menu JS v1.0 Stable Click-Only Owner Fix
 *
 * File: assets/header-menu.js
 *
 * Purpose:
 * - Preserve Shopify-native header-menu behavior unless FAR click-only mode is active.
 * - In FAR click-only mode:
 *   - Desktop submenu opens only by click.
 *   - Hover alone does not open the submenu.
 *   - Moving the mouse away does not close the submenu.
 *   - Clicking the same parent item closes it.
 *   - Clicking another parent item switches the open submenu.
 *   - Clicking outside the header/menu closes it.
 *   - Escape closes it.
 * - Preserve mobile drawer behavior.
 * - Preserve overflow calculations.
 * - Preserve image preloading.
 * - Preserve Shopify resize/menu-style behavior.
 * - No setInterval.
 * - No heavy observer loop.
 */

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    this.addEventListener('click', this.#farClickOnlyMenuListener, true);
    document.addEventListener('click', this.#farDocumentClickListener, true);
    document.addEventListener('keydown', this.#farKeydownListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);

    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }

    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);

    this.removeEventListener('click', this.#farClickOnlyMenuListener, true);
    document.removeEventListener('click', this.#farDocumentClickListener, true);
    document.removeEventListener('keydown', this.#farKeydownListener);

    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Detect FAR click-only mode.
   * This is set by the FAR section/header JS through data-far-click-menu.
   */
  #isFarClickOnlyMode() {
    const header = this.headerComponent;
    if (!header) return false;

    return (
      header.dataset.farClickMenu === 'true' ||
      header.dataset.farClickOnlyOwnerBooted === 'true'
    );
  }

  #isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  #overflowSubmenuListener = () => {
    if (this.#isFarClickOnlyMode() && this.#isDesktop()) return;
    this.#deactivate(undefined, { force: true });
  };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (this.#isFarClickOnlyMode() && this.#isDesktop()) return;

    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    if (this.#isFarClickOnlyMode() && this.#isDesktop()) return;

    const { x, y } = this.#lastPointer;

    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;

      const listItem = target.closest('.menu-list__list-item');

      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (this.#isFarClickOnlyMode() && this.#isDesktop()) return;

    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;

    item.style.setProperty(
      '--box-height',
      `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`
    );
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu.
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow list is hovered.
   * @returns {boolean}
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ??
      false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * FAR desktop click-only listener.
   * @param {MouseEvent} event
   */
  #farClickOnlyMenuListener = (event) => {
    if (!this.#isFarClickOnlyMode() || !this.#isDesktop()) return;
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.menu-list__link, summary, a, button');
    if (!(trigger instanceof Element) || !this.contains(trigger)) return;

    const item = findMenuItem(trigger) || findMenuItem(event.target);
    if (!item) return;

    const submenu = findSubmenu(item);
    const hasSubmenu =
      Boolean(submenu) ||
      item.matches?.('details') ||
      trigger.getAttribute('aria-haspopup') === 'true' ||
      trigger.getAttribute('aria-haspopup') === 'menu';

    if (!hasSubmenu) {
      this.#deactivate(undefined, { force: true });
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    if (item === this.#state.activeItem) {
      this.#deactivate(item, { force: true });
      return;
    }

    this.#activateItem(item, trigger.slot === '');
  };

  /**
   * FAR outside click listener.
   * @param {MouseEvent} event
   */
  #farDocumentClickListener = (event) => {
    if (!this.#isFarClickOnlyMode() || !this.#isDesktop()) return;
    if (!this.#state.activeItem) return;

    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#deactivate(undefined, { force: true });
  };

  /**
   * FAR escape key listener.
   * @param {KeyboardEvent} event
   */
  #farKeydownListener = (event) => {
    if (!this.#isFarClickOnlyMode() || !this.#isDesktop()) return;
    if (event.key !== 'Escape') return;

    this.#deactivate(undefined, { force: true });
  };

  /**
   * Activate the selected menu item immediately.
   * Native theme hover/focus activation is ignored in FAR click-only desktop mode.
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (this.#isFarClickOnlyMode() && this.#isDesktop() && event.type !== 'click') return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.#activateItem(item, isDefaultSlot);
  };

  /**
   * Shared activation logic.
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   */
  #activateItem(item, isDefaultSlot = true) {
    if (!item || !this.headerComponent) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem && previouslyActiveItem !== item) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.removeAttribute('data-far-click-open');

      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
        previousSubmenu.removeAttribute('data-far-click-open');
      }

      this.#stopPointerTracking(previouslyActiveItem);
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    item.setAttribute('data-far-click-open', 'true');

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
      submenu.setAttribute('data-far-click-open', 'true');

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });

      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');

    if (!(this.#isFarClickOnlyMode() && this.#isDesktop())) {
      this.#startPointerTracking(item, previouslyActiveItem);
    }
  }

  /**
   * Deactivate the active item after a delay.
   * Native pointerleave/focusout deactivation is ignored in FAR click-only desktop mode.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (this.#isFarClickOnlyMode() && this.#isDesktop()) return;
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }

      return;
    }

    this.#deactivate(undefined, { force: true });
  }

  /**
   * Deactivate the active item immediately.
   * @param {HTMLElement | null | undefined} item
   * @param {{ force?: boolean }} options
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (this.#isFarClickOnlyMode() && this.#isDesktop() && !options.force) return;

    const activeItem = item || this.#state.activeItem;

    if (!activeItem || activeItem !== this.#state.activeItem) return;

    if (!this.#isFarClickOnlyMode() && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) {
      return;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(activeItem);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(activeItem);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    activeItem.ariaExpanded = 'false';
    activeItem.removeAttribute('data-far-click-open');

    if (submenu) {
      delete submenu.dataset.active;
      submenu.removeAttribute('data-far-click-open');
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();

    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));

        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });

    const height = this.overflowMenu?.offsetHeight || 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });

    return height;
  }

  /**
   * Calculate and set the full open header height.
   * If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector('.header__row--top')
          )?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const directMenuItem = element.querySelector('[ref="menuitem"]');

  if (directMenuItem instanceof HTMLElement) {
    return directMenuItem;
  }

  const closestMenuItemHost = element.closest('.menu-list__list-item, li, details');

  if (closestMenuItemHost instanceof HTMLElement) {
    const nestedMenuItem = closestMenuItemHost.querySelector('[ref="menuitem"]');

    if (nestedMenuItem instanceof HTMLElement) {
      return nestedMenuItem;
    }
  }

  return element instanceof HTMLElement ? element : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu =
    element?.parentElement?.querySelector('[ref="submenu[]"]') ||
    element?.querySelector?.('[ref="submenu[]"], .menu-list__submenu, .mega-menu, .mega-menu__list');

  return submenu instanceof HTMLElement ? submenu : null;
}